// src/context/NotificationContext.jsx
import React, { createContext, useContext, useEffect, useState, useMemo, useCallback, useRef } from "react";
// ✅ FIX: Added setDoc, serverTimestamp imports
import { collection, query, orderBy, onSnapshot, writeBatch, doc, getDocs, where, limit, setDoc, serverTimestamp } from "firebase/firestore";
// ✅ FIX: Added messaging imports
import { getMessaging, getToken } from "firebase/messaging";
import { db } from "@/firebase/firebase";
import { useAuth } from "./AuthContext";
import toast from "react-hot-toast";

const defaultContextValue = {
  notifications: [],
  unreadCount: 0,
  loadingNotifications: true,
  markNotificationsAsRead: async () => {},
  markAllNotificationsAsRead: async () => {},
  archiveNotification: async () => {},
  emitEphemeral: () => {},
};

const NotificationContext = createContext(defaultContextValue);
export const useNotifications = () => useContext(NotificationContext);

// Helper functions
const channelKeyForUid = (uid) => (uid ? `scrimly-notifications:${uid}` : null);

function useInteractionGate() {
  const interacted = useRef(false);
  useEffect(() => {
    const mark = () => (interacted.current = true);
    window.addEventListener("pointerdown", mark, { once: true });
    window.addEventListener("keydown", mark, { once: true });
    return () => {
      window.removeEventListener("pointerdown", mark);
      window.removeEventListener("keydown", mark);
    };
  }, []);
  return interacted;
}

function getActiveChatIdFromPath() {
  try {
    const p = window.location?.pathname || "";
    const m = p.match(/^\/messages\/([^/]+)/);
    return m?.[1] || null;
  } catch {
    return null;
  }
}

export const NotificationProvider = ({ children }) => {
  const { firebaseUser } = useAuth();
  const uid = firebaseUser?.uid || null;

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(true);

  const interacted = useInteractionGate();
  const lastTopIdRef = useRef(null);
  const mountedRef = useRef(false);
  const bcRef = useRef(null);

  const closeBC = useCallback(() => { try { bcRef.current?.close?.(); } catch {} bcRef.current = null; }, []);
  
  const ensureBC = useCallback((targetUid) => {
    if (typeof window === "undefined" || !("BroadcastChannel" in window)) return null;
    if (!targetUid) return null;
    const key = channelKeyForUid(targetUid);
    if (!key) return null;
    if (bcRef.current && bcRef.current.__key === key) return bcRef.current;
    closeBC();
    try { const bc = new BroadcastChannel(key); bc.__key = key; bcRef.current = bc; return bc; } catch { return null; }
  }, [closeBC]);

  const playCue = useCallback(() => {
    try {
      if (!interacted.current) return;
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = "sine"; o.frequency.value = 880; o.connect(g); g.connect(ctx.destination);
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.06, ctx.currentTime + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.18);
      o.start(); o.stop(ctx.currentTime + 0.2);
    } catch {}
  }, [interacted]);

  const shouldSuppressToast = useCallback((payload) => {
    try {
      const type = payload?.data?.type || payload?.type || "";
      if (type !== "new_chat_message") return false;
      const incomingChatId = payload?.data?.chatId || null;
      const activeChatId = getActiveChatIdFromPath();
      if (incomingChatId && activeChatId && incomingChatId === activeChatId) return true;
      return false;
    } catch { return false; }
  }, []);

  const showToast = useCallback((payload) => {
    if (!payload) return;
    const type = payload?.data?.type || payload?.type || "notification";
    const body = payload?.notification?.body || payload?.data?.body || payload?.title || "You have a new notification";
    const title = payload?.notification?.title || payload?.data?.title || (type === "scrimmage_interest_received" ? "New Team Interest" : type === "message" || type === "new_chat_message" ? "New Message" : "Notification");
    
    toast(() => (
      <div className="text-sm">
        <div className="font-semibold">{title}</div>
        <div className="text-zinc-600 dark:text-zinc-300">{body}</div>
      </div>
    ), { duration: 3500 });
  }, []);

  const emitEphemeral = useCallback((payload) => {
    if (shouldSuppressToast(payload)) return;
    showToast(payload);
    playCue();
    const bc = ensureBC(uid);
    bc?.postMessage({ type: "EPHEMERAL_TOAST", uid, payload });
  }, [ensureBC, playCue, showToast, uid, shouldSuppressToast]);

  // ✅ FIX: Auto-healing token sync to the correct Sub-Collection
  useEffect(() => {
    async function syncToken() {
      if (!uid || typeof window === 'undefined') return;

      // Web-only flow. In the Capacitor native WebView the `Notification`
      // web API doesn't exist (FCM is handled by usePushNotifications via
      // the native plugin instead), so referencing `Notification.permission`
      // here threw "ReferenceError: Notification is not defined" on every
      // app load. Bail out cleanly when the API isn't present.
      if (typeof Notification === 'undefined') return;

      try {
        if (Notification.permission === 'granted') {
          const messaging = getMessaging();
          // ⚠️ Ensure your VAPID key is set in your env vars
          const currentToken = await getToken(messaging, {
            vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY 
          });

          if (currentToken) {
            // Write to users/{uid}/fcmTokens/{token}
            // This matches exactly where utils.js looks for tokens
            const tokenRef = doc(db, `users/${uid}/fcmTokens`, currentToken);
            await setDoc(tokenRef, {
              token: currentToken,
              platform: 'web',
              lastSyncedAt: serverTimestamp() 
            }, { merge: true });
            
            console.log("Token synced to sub-collection:", currentToken);
          }
        }
      } catch (err) {
        console.error("Token sync failed:", err);
      }
    }

    syncToken();
  }, [uid]);

  useEffect(() => {
    if (!uid) {
      setNotifications([]);
      setUnreadCount(0);
      setLoadingNotifications(false);
      lastTopIdRef.current = null;
      mountedRef.current = false;
      closeBC();
      return;
    }

    setLoadingNotifications(true);
    ensureBC(uid);

    const ref = collection(db, `users/${uid}/notifications`);
    const q = query(ref, orderBy("createdAt", "desc"), limit(50));

    const unsubscribe = onSnapshot(q, (snapshot) => {
        let currentUnread = 0;

        const items = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();

          // --- FIX: Filter out Deleted/Dismissed items ---
          const isHidden = 
            data.deleted === true || 
            data.dismissed === true || 
            data.isArchived === true || 
            data.archived === true;

          // Only count as unread if it is NOT hidden AND is unread
          if (!isHidden && data.isRead === false) {
            currentUnread++;
          }

          const ts = data.createdAt || data.timestamp || null;

          return {
            id: docSnap.id,
            ...data,
            timestamp: typeof ts?.toDate === 'function' ? ts.toDate() : (ts instanceof Date ? ts : null),
          };
        });

        const topId = items[0]?.id || null;
        const hadTop = lastTopIdRef.current !== null;
        if (mountedRef.current && hadTop && topId && topId !== lastTopIdRef.current) {
          playCue();
        }
        lastTopIdRef.current = topId;

        setNotifications(items);
        setUnreadCount(currentUnread);
        setLoadingNotifications(false);
        bcRef.current?.postMessage?.({ type: "NOTIF_UNREAD", uid, count: currentUnread });
      },
      (err) => {
        console.error("Failed to load notifications", err);
        setLoadingNotifications(false);
      }
    );

    mountedRef.current = true;
    return () => unsubscribe();
  }, [uid, closeBC, ensureBC, playCue]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onPush = (e) => {
      const payload = e?.detail;
      if (!payload) return;
      const payloadUid = payload?.data?.uid || null;
      if (payloadUid && uid && payloadUid !== uid) return;
      if (shouldSuppressToast(payload)) return;
      showToast(payload);
      playCue();
      const bc = ensureBC(uid);
      bc?.postMessage({ type: "EPHEMERAL_TOAST", uid, payload });
    };
    window.addEventListener("scrimly:push", onPush);
    return () => window.removeEventListener("scrimly:push", onPush);
  }, [ensureBC, playCue, showToast, uid, shouldSuppressToast]);

  useEffect(() => {
    if (!uid) return;
    const bc = ensureBC(uid);
    if (!bc) return;
    const onMsg = (e) => {
      const msg = e?.data || {};
      const { type, payload, count, uid: msgUid } = msg;
      if (msgUid && msgUid !== uid) return;
      if (type === "EPHEMERAL_TOAST") {
        if (shouldSuppressToast(payload)) return;
        showToast(payload);
        playCue();
      } else if (type === "NOTIF_UNREAD") {
        setUnreadCount((c) => (typeof count === "number" ? count : c));
      }
    };
    bc.addEventListener("message", onMsg);
    return () => bc.removeEventListener("message", onMsg);
  }, [ensureBC, playCue, showToast, uid, shouldSuppressToast]);

  const markNotificationsAsRead = useCallback(async (notificationIds = []) => {
      if (!uid || !Array.isArray(notificationIds) || !notificationIds.length) return;
      const batch = writeBatch(db);
      notificationIds.forEach((id) => {
        if (!id) return;
        batch.update(doc(db, `users/${uid}/notifications`, id), { isRead: true });
      });
      try { await batch.commit(); } catch { toast.error("Failed to mark as read."); }
    }, [uid]);

  const markAllNotificationsAsRead = useCallback(async () => {
    if (!uid) return;
    const ref = collection(db, `users/${uid}/notifications`);
    const q = query(ref, where("isRead", "==", false));
    try {
      const snapshot = await getDocs(q);
      if (snapshot.empty) return;
      const batch = writeBatch(db);
      snapshot.docs.forEach((docSnap) => batch.update(docSnap.ref, { isRead: true }));
      await batch.commit();
    } catch { toast.error("Failed to mark all as read."); }
  }, [uid]);

  // Archive (a.k.a. "Clear") a single notification. The bell dropdown's
  // "Clear" button fans this out across every visible notif so the bell
  // shade empties. Without this, the dropdown had an optimistic-hide
  // that died on app restart and notifications reappeared — the Clear
  // call was hitting `archiveNotification` which wasn't exported from
  // this context at all (silent failure: "archiveNotification function
  // missing from Context").
  //
  // Field shape matches the full /notifications page's archive flow
  // (Notifications.jsx) so a notif archived from either surface stays
  // archived consistently:
  //   • dismissed: true
  //   • isArchived: true
  //   • archivedAt: serverTimestamp()
  const archiveNotification = useCallback(async (notificationId) => {
    if (!uid || !notificationId) return;
    try {
      await setDoc(
        doc(db, `users/${uid}/notifications`, notificationId),
        {
          dismissed: true,
          isArchived: true,
          archivedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (err) {
      console.warn("[NotificationContext] archive failed", err);
      toast.error("Failed to clear notification.");
      throw err;
    }
  }, [uid]);

  const value = useMemo(() => ({
      notifications,
      unreadCount, // This is now the "Safe" count
      loadingNotifications,
      markNotificationsAsRead,
      markAllNotificationsAsRead,
      archiveNotification,
      emitEphemeral,
  }), [notifications, unreadCount, loadingNotifications, markNotificationsAsRead, markAllNotificationsAsRead, archiveNotification, emitEphemeral]);

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};